import React, { useState, useEffect } from 'react'
import { Box } from '@chakra-ui/react'

import {
    ClientFieldsFragment,
    PatientFieldsFragment,
    ActionItemFieldsFragment,
    useUpdateAppointmentClientPatientMutation,
    useUpdateActionItemMutation,
    ActionItemStatus,
    namedOperations,
    ActionItemType,
} from '~graphql/generated/graphql'
import ClientSelect from './client-select'
import PatientSelect from './patient-select'
import PatientInfoProvider from './patient-info-context'
import AppointmentDetailsSection from './appointment-details'
import SharedDrawer from '~components/shared/modals/shared-drawer'
import SharedStack from '~components/shared/wrappers/shared-stack'
import { useToastFeedback } from '~utils/hooks/use-toast-feedback'
import { useActionItems } from '~contexts/action-item-provider'

interface Props {
    isOpen: boolean
    onClose: () => void
    actionItem: ActionItemFieldsFragment
}

export default function MissingEntityDrawer({
    isOpen,
    onClose,
    actionItem,
}: Props) {
    const { refetch } = useActionItems()
    const { onCompleted, onError } = useToastFeedback(
        "Client's appointment updated successfully",
        "Error updating client's appointment"
    )
    const { type, pending_appointment } = actionItem

    const isMissingClient = type === ActionItemType.MissingClient

    const [updateActionItem] = useUpdateActionItemMutation({
        variables: {
            id: actionItem.id,
            data: {
                status: ActionItemStatus.Completed,
            },
        },
        refetchQueries: [namedOperations.Query.countActionItems],
        onCompleted: () => refetch(),
    })

    const [updateAppointmentClientAndPatient, { loading }] =
        useUpdateAppointmentClientPatientMutation({
            onCompleted: () => {
                updateActionItem()
                onCompleted()
                onClose()
            },
            onError,
        })

    const [selectedClient, setSelectedClient] = useState<
        ClientFieldsFragment | undefined
    >(!isMissingClient ? pending_appointment?.client ?? undefined : undefined)
    const [selectedPatient, setSelectedPatient] =
        useState<PatientFieldsFragment>()

    const [isMakingNewClient, setIsMakingNewClient] = useState(false)
    const [isMakingNewPatient, setIsMakingNewPatient] = useState(false)

    useEffect(() => {
        if (!selectedClient && selectedPatient) {
            setSelectedPatient(undefined)
        }
    }, [selectedClient])

    useEffect(() => {
        setIsMakingNewPatient(isMakingNewClient)
    }, [isMakingNewClient])

    function handleCancel() {
        setSelectedClient(undefined)
        setSelectedPatient(undefined)
        setIsMakingNewClient(false)
        setIsMakingNewPatient(false)
    }

    async function handleUpdateAppointmentClientAndPatient() {
        if (!selectedClient && !isMakingNewClient) return
        if (!selectedPatient && !isMakingNewPatient) return

        const clientId = selectedClient?.id || undefined
        const patientId = selectedPatient?.id || undefined

        await updateAppointmentClientAndPatient({
            variables: {
                id: pending_appointment?.id || '',
                clientId,
                patientId,
            },
        })
    }

    return (
        <PatientInfoProvider
            info={pending_appointment?.patient_info || undefined}
        >
            <SharedDrawer
                isOpen={isOpen}
                onClose={onClose}
                isLoading={loading}
                onCancel={handleCancel}
                onConfirm={handleUpdateAppointmentClientAndPatient}
                isConfirmDisabled={
                    (!selectedClient && !isMakingNewClient) ||
                    (!selectedPatient && !isMakingNewPatient)
                }
                size="lg"
                title={isMissingClient ? 'Missing Client' : 'Missing Patient'}
                bodyOverrides={{
                    position: 'relative',
                    px: 0,
                    py: 0,
                }}
                warnBeforeClose={
                    !!selectedClient ||
                    !!selectedPatient ||
                    isMakingNewClient ||
                    isMakingNewPatient
                }
            >
                {!pending_appointment ? (
                    <Box>No appointment found</Box>
                ) : (
                    <>
                        <AppointmentDetailsSection
                            appointment={pending_appointment}
                            isMissingClient={isMissingClient}
                            client={selectedClient}
                            setClient={setSelectedClient}
                            patient={selectedPatient}
                            setPatient={setSelectedPatient}
                            isMakingNewClient={isMakingNewClient}
                            setMakingNewClient={setIsMakingNewClient}
                            isMakingNewPatient={isMakingNewPatient}
                            setMakingNewPatient={setIsMakingNewPatient}
                        />
                        <SharedStack spacing={6} px={6} py={4}>
                            {isMissingClient && (
                                <ClientSelect
                                    appointment={pending_appointment}
                                    client={selectedClient}
                                    setClient={setSelectedClient}
                                    isMakingNewClient={isMakingNewClient}
                                    setIsMakingNewClient={setIsMakingNewClient}
                                />
                            )}
                            <PatientSelect
                                appointment={pending_appointment}
                                isMissingClient={isMissingClient}
                                client={selectedClient}
                                patient={selectedPatient}
                                setPatient={setSelectedPatient}
                                isMakingNewClient={isMakingNewClient}
                                isMakingNewPatient={isMakingNewPatient}
                                setIsMakingNewPatient={setIsMakingNewPatient}
                            />
                        </SharedStack>
                    </>
                )}
            </SharedDrawer>
        </PatientInfoProvider>
    )
}
